import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import contractService from '../../../services/contractService';
import toast from 'react-hot-toast';

const SendContract = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = !!id;

    const [formData, setFormData] = useState({
        pjo_id: [],
        contract_name: '',
        subject: '',
        contract_template: '',
        message: '',
        remark: ''
    });

    const [pjos, setPjos] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pjosLoading, setPjosLoading] = useState(true);
    const [templatesLoading, setTemplatesLoading] = useState(true);

    const [errors, setErrors] = useState({});
    const [activeTooltip, setActiveTooltip] = useState(null);
    
    // Searchable dropdown state
    const [searchTerm, setSearchTerm] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [filteredPjos, setFilteredPjos] = useState([]);

    // Fetch PJOs and contract details if in edit mode
    useEffect(() => {
        const loadInitialData = async () => {
            try {
                // Fetch Airliners from separate endpoint
                const airlinerResponse = await contractService.getAirliners();
                if (airlinerResponse.success) {
                    const formattedPjos = (airlinerResponse.data || []).map(pjo => ({
                        id: String(pjo.id),
                        label: `${pjo.first_name}${pjo.last_name ? ' ' + pjo.last_name : ''} (${pjo.email})`
                    }));
                    setPjos(formattedPjos);
                }
            } catch (err) {
                toast.error('Failed to load Airliners');
            } finally {
                setPjosLoading(false);
            }

            // Fetch Templates in separate block for resilience
            try {
                setTemplatesLoading(true);
                const templatesResponse = await contractService.getTemplates();
                if (templatesResponse.success) {
                    // Ensure we are using an array from the data property
                    const templateData = Array.isArray(templatesResponse.data) ? templatesResponse.data : (templatesResponse.data?.templates || []);
                    setTemplates(templateData);
                }
            } catch (err) {
                console.error("Templates fetch failed:", err);
                // Don't show toast for templates 500 error since it might be a known backend issue
            } finally {
                setTemplatesLoading(false);
            }

            try {
                if (isEdit) {
                    const detailResponse = await contractService.getDetail(id);
                    if (detailResponse.success) {
                        const { contract } = detailResponse.data;
                        setFormData({
                            // Robust mapping for signers with type coercion to strings for MultiSelect matching
                            pjo_id: (contract.contractPeople || []).map(cp => String(cp.user_id || cp.pjo_id || cp.id)),
                            contract_name: contract.contract_name || '',
                            subject: contract.subject || '',
                            contract_template: contract.contract_template || '',
                            message: contract.message || '',
                            remark: contract.remark || ''
                        });
                    }
                }
            } catch (err) {
                toast.error('Failed to load contract details');
            }
        };

        loadInitialData();
    }, [isEdit, id]);

    // Filter PJOs based on search term
    useEffect(() => {
        if (searchTerm) {
            const filtered = pjos.filter(pjo => 
                pjo.label.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredPjos(filtered);
        } else {
            setFilteredPjos(pjos);
        }
    }, [searchTerm, pjos]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isDropdownOpen && !event.target.closest('.searchable-dropdown')) {
                setIsDropdownOpen(false);
                setSearchTerm('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isDropdownOpen]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        // Specific logic for template selection
        if (name === 'contract_template' && value) {
            const selectedTemplate = templates.find(t => (t.templateId || t.template_id) === value);
            if (selectedTemplate) {
                setFormData(prev => ({
                    ...prev,
                    [name]: value,
                    // Auto-fill subject and message if they are empty
                    subject: prev.subject || selectedTemplate.subject || '',
                    message: prev.message || selectedTemplate.message || ''
                }));
                // Clear errors
                setErrors(prev => ({ ...prev, [name]: null, subject: null, message: null }));
                return;
            }
        }

        setFormData(prev => ({ ...prev, [name]: value }));
        // Clear error when user types
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const handleBack = () => {
        navigate('/admin/contracts');
    };

    const handleSelectSigner = (pjoId) => {
        setFormData(prev => ({ ...prev, pjo_id: [pjoId] }));
        setIsDropdownOpen(false);
        setSearchTerm('');
        if (errors.pjo_id) {
            setErrors(prev => ({ ...prev, pjo_id: null }));
        }
    };

    const getSelectedSignerLabel = () => {
        if (!formData.pjo_id || formData.pjo_id.length === 0) return '';
        const selectedPjo = pjos.find(p => p.id === formData.pjo_id[0]);
        return selectedPjo ? selectedPjo.label : '';
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.pjo_id || formData.pjo_id.length === 0) newErrors.pjo_id = "Please select a signer.";
        if (!formData.contract_name) newErrors.contract_name = "The Contract name field is required.";
        if (!formData.subject) newErrors.subject = "The Subject field is required.";
        if (!formData.contract_template) newErrors.contract_template = "The Contract template field is required.";
        if (!formData.message) newErrors.message = "The Message field is required.";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async (shouldNavigate = true, saveAndSend = false) => {
        if (!validateForm()) {
            toast.error('Please fix the errors in the form');
            return;
        }

        setLoading(true);
        try {
            if (isEdit) {
                // For updates, send all modifiable fields including signers
                const updateData = {
                    pjo_id: formData.pjo_id,
                    contract_name: formData.contract_name,
                    subject: formData.subject,
                    message: formData.message,
                    save_and_send: saveAndSend
                };
                const result = await contractService.update(id, updateData);
                if (result.success) {
                    toast.success(saveAndSend ? 'Contract sent successfully' : 'Contract saved successfully');
                } else {
                    throw new Error(result.message || 'Failed to update contract');
                }
            } else {
                const createData = {
                    ...formData,
                    save_and_send: saveAndSend
                };
                const result = await contractService.create(createData);
                if (result.success) {
                    toast.success(saveAndSend ? 'Contract sent successfully' : 'Contract saved successfully');
                } else {
                    throw new Error(result.message || 'Failed to create contract');
                }
            }
            if (shouldNavigate) {
                navigate('/admin/contracts');
            }
        } catch (err) {
            toast.error(err.message || 'Failed to save contract');
        } finally {
            setLoading(false);
        }
    };

    const Tooltip = ({ text, id }) => (
        <div
            className={`absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-[#333] text-white text-[11px] rounded shadow-lg whitespace-nowrap z-[60] transition-opacity duration-200 ${activeTooltip === id ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        >
            {text}
            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#333]"></div>
        </div>
    );

    return (
        <div className="p-4 bg-[#f4f7f6] min-h-screen font-sans">
            {/* Breadcrumb */}
            <div className="mb-3 flex items-center gap-2 text-[12px] text-gray-500 font-medium">
                <i className="fas fa-file-contract"></i>
                <span>Contracts</span>
                <i className="fas fa-chevron-right text-[8px]"></i>
                <span>{isEdit ? 'Edit Contract Details' : 'Send Contract'}</span>
            </div>

            <div className="bg-white rounded shadow-sm overflow-hidden">
                {/* Header */}
                <div className="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <h2 className="text-lg font-bold text-gray-800">{isEdit ? 'Edit Contract Details' : 'Send Contract'}</h2>
                    </div>
                    <button
                        onClick={handleBack}
                        className="flex items-center gap-2 px-6 py-2 text-sm font-bold text-black bg-[#ffeb00] hover:bg-[#ffd200] rounded shadow-sm transition-colors uppercase tracking-tight"
                    >
                        <i className="fas fa-arrow-left"></i>
                        <span>Back</span>
                    </button>
                </div>

                {/* Form Container */}
                <div className="p-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-5 mb-4">
                        {/* Select Signer (Searchable Dropdown) */}
                        <div className="flex flex-col gap-1">
                            <label className="text-[13px] font-bold text-gray-800 flex items-center gap-1">
                                Select Signer<span className="text-red-500">*</span>
                                <div className="relative">
                                    <i
                                        className="fas fa-info-circle text-gray-400 text-[10px] cursor-help"
                                        onMouseEnter={() => setActiveTooltip('pjo_id')}
                                        onMouseLeave={() => setActiveTooltip(null)}
                                    ></i>
                                    <Tooltip text="Select the Airliner (PJO) who needs to sign." id="pjo_id" />
                                </div>
                            </label>
                            
                            {/* Custom Searchable Dropdown */}
                            <div className="relative searchable-dropdown">
                                {/* Selected Value Display */}
                                <div
                                    onClick={() => !pjosLoading && setIsDropdownOpen(!isDropdownOpen)}
                                    className={`w-full px-3 py-2 bg-white border ${errors.pjo_id ? 'border-red-500' : 'border-gray-300'} rounded focus:border-[#ffeb00] cursor-pointer text-sm font-medium h-[38px] flex items-center justify-between ${pjosLoading ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                                >
                                    <span className={getSelectedSignerLabel() ? 'text-gray-900' : 'text-gray-400'}>
                                        {pjosLoading ? 'Loading Airliners...' : (getSelectedSignerLabel() || 'Select Signer')}
                                    </span>
                                    <i className={`fas fa-chevron-down text-gray-400 text-xs transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
                                </div>

                                {/* Dropdown Menu */}
                                {isDropdownOpen && !pjosLoading && (
                                    <div className="absolute z-[9999] w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-[300px] overflow-hidden">
                                        {/* Search Input */}
                                        <div className="p-2 border-b border-gray-200 bg-white">
                                            <input
                                                type="text"
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                placeholder="Search..."
                                                className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:border-[#ffeb00] focus:outline-none"
                                                autoFocus
                                            />
                                        </div>

                                        {/* Options List - Scrollable */}
                                        <div className="overflow-y-auto max-h-[240px] overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
                                            {filteredPjos.length > 0 ? (
                                                <>
                                                    {/* Placeholder option */}
                                                    <div
                                                        onClick={() => {
                                                            setFormData(prev => ({ ...prev, pjo_id: [] }));
                                                            setIsDropdownOpen(false);
                                                            setSearchTerm('');
                                                        }}
                                                        className="px-3 py-2 text-sm text-gray-400 hover:bg-gray-100 cursor-pointer"
                                                    >
                                                        {/* Select Signer */}
                                                    </div>
                                                    {filteredPjos.map(pjo => (
                                                        <div
                                                            key={pjo.id}
                                                            onClick={() => handleSelectSigner(pjo.id)}
                                                            className={`px-3 py-2 text-sm cursor-pointer hover:bg-yellow-300 ${formData.pjo_id[0] === pjo.id ? 'bg-yellow-300 font-semibold' : 'text-gray-700'}`}
                                                        >
                                                            {pjo.label}
                                                        </div>
                                                    ))}
                                                </>
                                            ) : (
                                                <div className="px-3 py-2 text-sm text-gray-400 text-center">
                                                    No results found
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                            
                            {errors.pjo_id && <span className="text-[11px] text-red-500 font-medium">{errors.pjo_id}</span>}
                        </div>

                        {/* Contract Title / Name */}
                        <div className="flex flex-col gap-1">
                            <label className="text-[13px] font-bold text-gray-800 flex items-center gap-1">
                                {isEdit ? 'Contract Name/Title' : 'Contract Name'}<span className="text-red-500">*</span>
                                <div className="relative">
                                    <i
                                        className="fas fa-info-circle text-gray-400 text-[10px] cursor-help"
                                        onMouseEnter={() => setActiveTooltip('title')}
                                        onMouseLeave={() => setActiveTooltip(null)}
                                    ></i>
                                    <Tooltip text="Contract name will be included in the signature request." id="title" />
                                </div>
                            </label>
                            <input
                                type="text"
                                name="contract_name"
                                placeholder={isEdit ? 'Contract name/title' : 'Contract name'}
                                value={formData.contract_name}
                                onChange={handleChange}
                                className={`w-full px-3 py-2 bg-white border ${errors.contract_name ? 'border-red-500' : 'border-gray-300'} rounded focus:border-[#ffeb00] focus:outline-none text-sm font-medium h-[38px] transition-all`}
                            />
                            {errors.contract_name && <span className="text-[11px] text-red-500 font-medium">{errors.contract_name}</span>}
                        </div>

                        {/* Subject */}
                        <div className="flex flex-col gap-1">
                            <label className="text-[13px] font-bold text-gray-800 flex items-center gap-1">
                                Subject<span className="text-red-500">*</span>
                                <div className="relative">
                                    <i
                                        className="fas fa-info-circle text-gray-400 text-[10px] cursor-help"
                                        onMouseEnter={() => setActiveTooltip('subject')}
                                        onMouseLeave={() => setActiveTooltip(null)}
                                    ></i>
                                    <Tooltip text="Subject will be send as signature email subject." id="subject" />
                                </div>
                            </label>
                            <input
                                type="text"
                                name="subject"
                                placeholder="Subject"
                                value={formData.subject}
                                onChange={handleChange}
                                className={`w-full px-3 py-2 bg-white border ${errors.subject ? 'border-red-500' : 'border-gray-300'} rounded focus:border-[#ffeb00] focus:outline-none text-sm font-medium h-[38px] transition-all`}
                            />
                            {errors.subject && <span className="text-[11px] text-red-500 font-medium">{errors.subject}</span>}
                        </div>

                        {/* Contract Template (Required) */}
                        <div className="flex flex-col gap-1">
                            <label className="text-[13px] font-bold text-gray-800 flex items-center gap-1">
                                Contract Template<span className="text-red-500">*</span>
                                <div className="relative">
                                    <i
                                        className="fas fa-info-circle text-gray-400 text-[10px] cursor-help"
                                        onMouseEnter={() => setActiveTooltip('template')}
                                        onMouseLeave={() => setActiveTooltip(null)}
                                    ></i>
                                    <Tooltip text="Select a HelloSign template for the contract." id="template" />
                                </div>
                            </label>
                            <select
                                name="contract_template"
                                value={formData.contract_template}
                                onChange={handleChange}
                                disabled={templatesLoading}
                                className={`w-full px-3 py-2 bg-white border ${errors.contract_template ? 'border-red-500' : 'border-gray-300'} rounded focus:border-[#ffeb00] focus:outline-none text-sm font-medium h-[38px] transition-all ${templatesLoading ? 'bg-gray-50 cursor-not-allowed' : ''} appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22currentColor%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:16px] bg-[right_10px_center] bg-no-repeat`}
                            >
                                <option value="">{templatesLoading ? 'Loading templates...' : 'Select template'}</option>
                                {templates.map(tmp => (
                                    <option key={tmp.templateId || tmp.template_id} value={tmp.templateId || tmp.template_id}>
                                        {tmp.title || 'Untitled Template'}
                                    </option>
                                ))}
                                {isEdit && formData.contract_template && !templates.some(t => (t.templateId || t.template_id) === formData.contract_template) && (
                                    <option value={formData.contract_template}>
                                        {formData.contract_template}
                                    </option>
                                )}
                            </select>
                            {errors.contract_template && <span className="text-[11px] text-red-500 font-medium">{errors.contract_template}</span>}
                        </div>
                    </div>

                    {/* Message */}
                    <div className="flex flex-col gap-1 mb-6">
                        <label className="text-[13px] font-bold text-gray-800 flex items-center gap-1">
                            Message<span className="text-red-500">*</span>
                            <div className="relative">
                                <i
                                    className="fas fa-info-circle text-gray-400 text-[10px] cursor-help"
                                    onMouseEnter={() => setActiveTooltip('message')}
                                    onMouseLeave={() => setActiveTooltip(null)}
                                ></i>
                                <Tooltip text="Message will be send as signature email message." id="message" />
                            </div>
                        </label>
                        <textarea
                            name="message"
                            placeholder="Message"
                            rows={2}
                            value={formData.message}
                            onChange={handleChange}
                            className={`w-full px-3 py-2 bg-white border ${errors.message ? 'border-red-500' : 'border-gray-300'} rounded focus:border-[#ffeb00] focus:outline-none text-sm font-medium min-h-[60px]`}
                        ></textarea>
                        {errors.message && <span className="text-[11px] text-red-500 font-medium">{errors.message}</span>}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center justify-end gap-3 mt-8">
                        <button
                            onClick={() => handleSave(true, true)}
                            disabled={loading}
                            className={`px-8 py-2.5 text-sm font-bold text-black bg-[#ffeb00] hover:bg-[#ffd200] rounded shadow-md transition-all flex items-center justify-center gap-2 ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
                        >
                            {loading && <i className="fas fa-circle-notch fa-spin"></i>}
                            <span>Save And Send</span>
                        </button>
                        <button
                            onClick={() => handleSave(true, false)}
                            disabled={loading}
                            className={`px-8 py-2.5 text-sm font-bold text-black bg-[#ffeb00] hover:bg-[#ffd200] rounded shadow-md transition-all flex items-center justify-center gap-2 ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
                        >
                            {loading && <i className="fas fa-circle-notch fa-spin"></i>}
                            <span>Save</span>
                        </button>
                        <button
                            onClick={handleBack}
                            className="px-8 py-2.5 text-sm font-bold text-gray-700 bg-[#e9ecef] hover:bg-gray-200 rounded shadow-sm transition-all border border-gray-200"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SendContract;
